import { G } from '@mobily/ts-belt';
import Dialog from '@mui/material/Dialog';
import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { ReactElement, useCallback, useState } from 'react';
import { UseCreateMutateParams, useCreateController, useNotify, useRefresh } from 'react-admin';
import { match } from 'ts-pattern';

import {
  PostB2bEmployeeBulkCreateRequest,
  PostEmployeesBulkCreateValidateEndpoint,
} from '@Common/types/bo/b2b/employees/bulk_create';

import { Button } from '@sh4res/bo-common/components';
import { useBulkCreateErrorHandler } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/hooks/useBulkCreateErrorHandler';
import { useDialogOpenState } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/hooks/useDialogOpenState';
import { Errors } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/sections/Errors';
import { Overwrites } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/sections/Overwrites';
import { Results } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/sections/Results';
import { Upload } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/sections/Upload';
import { hasIgnoredOverrides } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/utils/hasIgnoredOverrides';
import { useCreatePromise, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { convertFileToBase64, disableBackdropClose } from '@sh4res/bo-common/utils';

type FileInputData = {
  rawFile: File;
  title: string;
};

export function B2bEmployeeBulkCreate({
  companyId,
  icon,
  ...props
}: CreateInDialogButtonProps['ButtonProps'] & {
  companyId?: number;
  icon?: ReactElement;
}) {
  const refresh = useRefresh();
  const notify = useNotify();
  const [create] = useCreatePromise();
  const resourceBasePath = useResourceBasePath();

  const { isOpen, handleOpen, handleClose } = useDialogOpenState();
  const { error, setError, onError } = useBulkCreateErrorHandler();

  const [validationResponse, setValidationResponse] = useState<
    PostEmployeesBulkCreateValidateEndpoint['Client']['ResponseBody'] | null
  >(null);
  const [bulkUploadResultId, setBulkUploadResultId] = useState<number | null>(null);
  const [isBulkUploadLoading, setBulkUploadLoading] = useState<boolean>(false);

  const [overrideData, setOverrideData] = useState<
    PostEmployeesBulkCreateValidateEndpoint['Client']['RequestBody'] | null
  >(null);

  const handleReset = useCallback(() => {
    setError(null);
    setOverrideData(null);
    setValidationResponse(null);
    setBulkUploadResultId(null);
  }, [setError]);

  const handleTransform = useCallback(
    async (data: {
      file: FileInputData;
      company_id: number;
      automatic_onboarding?: boolean;
    }): Promise<PostB2bEmployeeBulkCreateRequest> => {
      const fileBase64 = await convertFileToBase64(data.file);

      return {
        company_id: data.company_id,
        file_content: fileBase64.file ?? '',
        automatic_onboarding: data.automatic_onboarding ?? true,
      };
    },
    [],
  );

  const bulkCreate = useCallback(
    async (data: PostEmployeesBulkCreateValidateEndpoint['Client']['RequestBody']) => {
      try {
        setBulkUploadLoading(true);
        if (isBulkUploadLoading) {
          return;
        }
        const { b2b_employees_bulk_upload_result_id: b2bEmployeesBulkUploadResultId } =
          await create(`${resourceBasePath}employees/bulk_create`, { data });
        if (G.isNullable(b2bEmployeesBulkUploadResultId)) {
          notify('resources.bo_common.b2b.employees.actions.bulk_create.no_employees_affected', {
            type: 'success',
          });
          handleClose();
        }
        setBulkUploadResultId(b2bEmployeesBulkUploadResultId);
        setBulkUploadLoading(false);
        refresh();
      } catch (error) {
        onError(error);
      }
    },
    [
      setBulkUploadLoading,
      isBulkUploadLoading,
      create,
      resourceBasePath,
      notify,
      handleClose,
      setBulkUploadResultId,
      refresh,
      onError,
    ],
  );

  const onSuccess = useCallback(
    (
      data: PostEmployeesBulkCreateValidateEndpoint['Client']['ResponseBody'],
      variables: UseCreateMutateParams<
        PostEmployeesBulkCreateValidateEndpoint['Client']['RequestBody']
      >,
    ) => {
      setError(null);
      if (data.type === 'success') {
        if (hasIgnoredOverrides(data.ignored_overrides)) {
          notify(
            'resources.bo_common.b2b.employees.actions.bulk_create.sections.overwrites.ignored_fields_warning',
            { type: 'warning' },
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- it's set for sure
        bulkCreate(variables.data! as NonNullable<Required<typeof variables.data>>);
      } else {
        setValidationResponse(data);
        setOverrideData(variables.data as NonNullable<Required<typeof variables.data>>);
      }
    },
    [bulkCreate, notify, setValidationResponse, setOverrideData, setError],
  );

  const { save } = useCreateController({
    resource: `${resourceBasePath}employees/bulk_create_validate`,
    transform: handleTransform,
    mutationOptions: { onSuccess, onError },
  });

  const saveOverwrite = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- it's set for sure
    async () => bulkCreate(overrideData!),
    [bulkCreate, overrideData],
  );

  const dialogContent = match(validationResponse)
    .with(null, { type: 'success' }, () => (
      <Upload
        close={handleClose}
        save={async (data) => {
          await save?.(data);
        }}
        companyId={companyId}
        errorMessage={error}
      />
    ))
    .with({ type: 'overwrite_confirmation' }, (data) => (
      <Overwrites
        close={handleClose}
        cancel={handleReset}
        data={data}
        confirmOverwrite={saveOverwrite}
        errorMessage={error}
      />
    ))
    .with({ type: 'validation_error' }, (data) => (
      <Errors close={handleClose} cancel={handleReset} data={data} />
    ))
    .exhaustive();

  return (
    <>
      <Button
        size="small"
        startIcon={icon}
        onClick={handleOpen}
        label="resources.bo_common.b2b.employees.actions.bulk_create.label"
        permissions={{
          resource: `${resourceBasePath}employees/bulk_create`,
          action: 'create',
        }}
        data-testid="BoExternalB2bEmployeeBulkCreate_OpenButton"
        {...props}
      />
      <Dialog
        fullWidth
        maxWidth="md"
        open={isOpen}
        onClose={disableBackdropClose(handleClose)}
        onTransitionExited={handleReset}>
        {G.isNotNullable(bulkUploadResultId) ? (
          <Results bulkUploadResultId={bulkUploadResultId} close={handleClose} />
        ) : (
          dialogContent
        )}
      </Dialog>
    </>
  );
}
